import React from 'react';
import { SlidersHorizontal } from 'lucide-react';
import {
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '../../../components-ui/DropdownMenu';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '../../../components-ui/Command';
import Checkbox from '../../../components/Checkbox';
import Typography from '../../../components/Typography';
import { NameStyled } from './PerformanceChartHeader/PerformanceChartHeader.styles';

const DEFAULT_SEARCH_THRESHOLD = 5;

type SearchableFilterPanelProps<T> = {
  items: T[];
  getKey: (item: T) => string | number;
  getLabel: (item: T) => string;
  isActive: (item: T) => boolean;
  onToggle: (item: T) => void;
  searchPlaceholder?: string;
  /** Widens the panel to the search placeholder (up to 20rem, then an ellipsis); off by default. */
  fitSearchPlaceholder?: boolean;
  searchThreshold?: number;
  headerSlot?: React.ReactNode;
  /** Replaces the plain name; `getLabel` still drives the search and the row's accessible name. */
  renderItem?: (item: T) => React.ReactNode;
  /** Rendered after the rows, inside the list. Its cmdk items inherit `forceMount`, so no search hides them. */
  footerSlot?: React.ReactNode;
  /** cmdk's match function. Omitted, cmdk's fuzzy scorer ranks and reorders the matches. */
  filter?: React.ComponentPropsWithoutRef<typeof Command>['filter'];
};

export function SearchableFilterPanel<T>({
  items,
  getKey,
  getLabel,
  isActive,
  onToggle,
  searchPlaceholder = 'Search...',
  fitSearchPlaceholder = false,
  searchThreshold = DEFAULT_SEARCH_THRESHOLD,
  headerSlot,
  renderItem,
  footerSlot,
  filter,
}: SearchableFilterPanelProps<T>) {
  const showSearch = items.length >= searchThreshold;

  return (
    <Command filter={filter}>
      {showSearch && (
        <CommandInput
          placeholder={searchPlaceholder}
          autoFocus
          className="h-9"
          // `ch` is measured in the input's own font; the 20rem cap keeps the submenu on narrow screens.
          style={
            fitSearchPlaceholder
              ? { minWidth: `min(${searchPlaceholder.length}ch, 20rem)`, textOverflow: 'ellipsis' }
              : undefined
          }
        />
      )}
      <CommandList>
        {headerSlot}
        <CommandGroup>
          {items.map((item) => {
            const active = isActive(item);
            const label = getLabel(item);
            return (
              <CommandItem
                key={getKey(item)}
                value={label}
                // Named explicitly, as AdvancedFacetedFilter does: once the checkbox below is named,
                // a name computed from the row's content would read the label twice.
                aria-label={label}
                onSelect={() => onToggle(item)}
                className="flex w-full cursor-pointer justify-between"
              >
                {renderItem ? (
                  renderItem(item)
                ) : (
                  <NameStyled>
                    <Typography>{label}</Typography>
                  </NameStyled>
                )}
                <Checkbox style={{ padding: 4 }} checked={active} color="dark" inputProps={{ 'aria-label': label }} />
              </CommandItem>
            );
          })}
        </CommandGroup>
        {/* Grouped and force-mounted: no search hides it, and cmdk's sort can re-append a wrapped item. */}
        {footerSlot ? <CommandGroup forceMount>{footerSlot}</CommandGroup> : null}
      </CommandList>
    </Command>
  );
}

type SearchableFilterSubmenuProps<T> = SearchableFilterPanelProps<T> & {
  triggerLabel: string;
  disabled?: boolean;
  /** Drawn before the label in place of the default sliders glyph. */
  triggerIcon?: React.ReactNode;
};

export function SearchableFilterSubmenu<T>({
  triggerLabel,
  disabled,
  triggerIcon,
  ...panelProps
}: SearchableFilterSubmenuProps<T>) {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger disabled={disabled}>
        {triggerIcon ?? <SlidersHorizontal className="mr-2 h-4 w-4" />}
        <span>{triggerLabel}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent className="max-h-96 overflow-y-auto p-0" onKeyDown={(e) => e.stopPropagation()}>
          <SearchableFilterPanel {...panelProps} />
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}
